/**
 * An edge that cannot take the straight way without crossing a card.
 *
 * React Flow's smoothstep puts its vertical segment halfway between the two
 * handles. Between neighbouring columns that is the empty gutter and the line
 * is clean — but an edge that skips a column, or doubles back to one already
 * passed, has its midpoint inside a column, so it runs underneath whatever
 * cards are stacked there and the reader loses it.
 *
 * These go under the graph instead, along a channel the layout assigns: out of
 * the source, down below every card, across, and up into the target. Each such
 * edge gets its own channel, nested so the longest runs deepest, so they do not
 * land on each other either.
 *
 * The path is drawn here rather than with `getSmoothStepPath` because the point
 * is to choose where the vertical segments sit, which that helper decides for
 * itself.
 */

import React, { FC } from 'react';

/** How far the line leaves a handle before it turns. */
const STUB = 18;
/** Corner rounding; clamped per corner to half of the shorter leg. */
const RADIUS = 8;

interface Point {
	x: number;
	y: number;
}

/**
 * An orthogonal polyline with rounded corners.
 *
 * Each corner is a quadratic curve that starts and ends a radius short of the
 * turn, so a leg shorter than two radii would otherwise overshoot into the next
 * one — hence the clamp against both adjacent legs.
 */
const roundedPath = (points: Point[]): string => {
	if (points.length < 2) {
		return '';
	}

	const lengthOf = (from: Point, to: Point): number =>
		Math.abs(to.x - from.x) + Math.abs(to.y - from.y);

	const towards = (from: Point, to: Point, distance: number): Point => {
		const span = lengthOf(from, to);
		if (span === 0) {
			return { x: from.x, y: from.y };
		}
		const ratio = distance / span;
		return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
	};

	let path = `M ${points[0].x},${points[0].y}`;

	for (let index = 1; index < points.length - 1; index += 1) {
		const previous = points[index - 1];
		const corner = points[index];
		const next = points[index + 1];
		const radius = Math.min(RADIUS, lengthOf(previous, corner) / 2, lengthOf(corner, next) / 2);

		if (radius <= 0) {
			path += ` L ${corner.x},${corner.y}`;
			continue;
		}

		const entry = towards(corner, previous, radius);
		const exit = towards(corner, next, radius);
		path += ` L ${entry.x},${entry.y} Q ${corner.x},${corner.y} ${exit.x},${exit.y}`;
	}

	const last = points[points.length - 1];
	return `${path} L ${last.x},${last.y}`;
};

interface AOPRoutedEdgeProps {
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	markerEnd?: string;
	style?: React.CSSProperties;
	data?: { channelY: number };
}

export const AOPRoutedEdge: FC<AOPRoutedEdgeProps> = ({
	sourceX,
	sourceY,
	targetX,
	targetY,
	markerEnd,
	style,
	data,
}) => {
	// Without a channel there is nothing to route along, so the line goes
	// straight rather than disappearing.
	const channelY = data && data.channelY !== undefined ? data.channelY : null;

	const points: Point[] =
		channelY === null
			? [
					{ x: sourceX, y: sourceY },
					{ x: targetX, y: targetY },
			  ]
			: [
					{ x: sourceX, y: sourceY },
					{ x: sourceX + STUB, y: sourceY },
					{ x: sourceX + STUB, y: channelY },
					{ x: targetX - STUB, y: channelY },
					{ x: targetX - STUB, y: targetY },
					{ x: targetX, y: targetY },
			  ];

	// `react-flow__edge-path` is what the stylesheet dashes for a backwards edge
	// and greys for an implicit one, so the class has to stay on the path.
	return (
		<path
			className="react-flow__edge-path"
			d={roundedPath(points)}
			markerEnd={markerEnd}
			style={style}
			fill="none"
		/>
	);
};

export default AOPRoutedEdge;
